// src/pages/Platform/Chatbot/ChatbotBuilder/KnowledgeBase/Files/Files.js

import React, { useState, useEffect, useCallback } from 'react';
import ConfirmationModal from '../../../../../../components/ConfirmationModal';
import './Files.css';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFilePdf, faFileWord, faFileExcel, faFilePowerpoint,
  faFileImage, faFileArchive, faFileAlt, faSpinner, faCheckCircle, faTimesCircle
} from '@fortawesome/free-solid-svg-icons';
import { Upload, Trash2, Globe, RefreshCw } from 'react-feather';
import { auth } from '../../../../../../firebase/config/config';
import { useChatbotContext } from '../../../../../../context/ChatbotContext';
import { useUser } from '../../../../../../context/UserContext';
import logger from '../../../../../../utils/logger';
import KnowledgeBaseOverview from '../KnowledgeBaseOverview';

// Universal Components
import UniversalKnowledgeBasePanel from '../components/UniversalKnowledgeBasePanel';
import UniversalList, { UniversalListItem } from '../components/UniversalList';
import { StatusChip, ItemTitle } from '../components/CommonStyles';

// Material-UI imports
import {
  Typography,
  IconButton,
  LinearProgress,
  Box,
  Alert,
  Button,
  ListItemIcon,
  ListItemText,
  Checkbox
} from '@mui/material';
import { styled } from '@mui/material/styles';

// --- Styled Components ---

const UploadButton = styled(Button)(({ theme }) => ({
  width: '100%',
  backgroundColor: '#000000',
  color: '#ffffff',
  padding: '12px',
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 600,
  marginTop: '24px',
  '&:hover': {
    backgroundColor: '#333333',
  },
  '&:disabled': {
    backgroundColor: '#e2e8f0',
    color: '#94a3b8',
  },
}));

const ProgressContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  width: '150px',
}));

const ProgressText = styled(Typography)(({ theme }) => ({
  fontSize: '11px',
  fontWeight: 600,
  minWidth: '35px',
  textAlign: 'right',
  color: '#64748b',
}));

const SelectionBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 16px',
  backgroundColor: '#f8fafc',
  borderRadius: '8px',
  marginBottom: '16px',
  border: '1px solid #e2e8f0',
}));

const DeleteSelectedButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#ef4444',
  color: '#ffffff',
  padding: '8px 16px',
  borderRadius: '6px',
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '13px',
  '&:hover': {
    backgroundColor: '#dc2626',
  },
  '&:disabled': {
    backgroundColor: '#fecaca',
    color: '#ffffff',
  },
}));

function Files({
  uploadedFiles,
  handleFileUpload,
  selectedChatbotId: propSelectedChatbotId,
}) {
  const { chatbots } = useChatbotContext();
  const { incrementUsage, refreshUserData } = useUser();
  const [files, setFiles] = useState([]);
  const [selectedChatbotId, setSelectedChatbotId] = useState(propSelectedChatbotId || '');
  const [assignedFileIds, setAssignedFileIds] = useState(new Set());
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filesToDelete, setFilesToDelete] = useState([]);
  const [selectedFileIds, setSelectedFileIds] = useState(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const [crawlingSiteId, setCrawlingSiteId] = useState(null);

  // Update selectedChatbotId when prop changes
  useEffect(() => {
    if (propSelectedChatbotId) {
      setSelectedChatbotId(propSelectedChatbotId);
    }
  }, [propSelectedChatbotId]);

  // Fetch files assigned to the chatbot
  const fetchChatbotFiles = useCallback(async () => {
    if (!selectedChatbotId) {
      setAssignedFileIds(new Set());
      return;
    }

    try {
      const response = await axios.get(`/api/chatbot/${selectedChatbotId}/files`);
      const chatbotFiles = response.data.files || [];
      const fileIdSet = new Set(chatbotFiles.map(file => file.id.toString()));
      setAssignedFileIds(fileIdSet);
    } catch (error) {
      logger.error('Error fetching chatbot files:', error);
      setAssignedFileIds(new Set());
    }
  }, [selectedChatbotId]);

  const fetchUploadedFiles = useCallback(async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        setUploadError('User not authenticated');
        return;
      }

      if (selectedChatbotId) {
        // Strict View: Only fetch chatbot files
        const response = await axios.get(`/api/chatbot/${selectedChatbotId}/files`);
        // Show all files including uploaded and website crawled
        const uploadFiles = response.data.files || [];

        setFiles(uploadFiles);
        setAssignedFileIds(new Set(uploadFiles.map(f => f.id.toString())));
      } else {
        // Global View (Fallback)
        const response = await axios.get('/api/files', {
          headers: {
            'x-user-id': user.uid
          }
        });
        setFiles(response.data.files);
      }
    } catch (error) {
      logger.error('Error fetching files:', error);
      setUploadError('Failed to fetch files. Please try again later.');
    }
  }, [selectedChatbotId]);

  // Auto-select first chatbot when chatbots are loaded
  useEffect(() => {
    if (chatbots.length > 0 && !selectedChatbotId) {
      setSelectedChatbotId(chatbots[0].chatbot_id);
    }
  }, [chatbots, selectedChatbotId]);

  useEffect(() => {
    if (selectedChatbotId) {
      fetchUploadedFiles();
    }
  }, [selectedChatbotId, fetchUploadedFiles]);

  const handleUpload = async (e) => {
    let files = [];
    if (e.dataTransfer) {
      files = Array.from(e.dataTransfer.files);
    } else {
      files = Array.from(e.target.files);
    }

    if (!files.length) return;

    setUploading(true);
    setUploadError(null);

    // Validate files before upload
    const MAX_SIZE = 100 * 1024 * 1024; // 100MB
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
      'text/markdown',
      'text/x-markdown',
    ];

    const invalidFiles = files.filter(file => {
      const isMarkdown = file.name.toLowerCase().endsWith('.md');
      const isAllowedType = allowedTypes.includes(file.type);
      const isAllowedSize = file.size <= MAX_SIZE;

      if (isMarkdown && isAllowedSize) return false; // Allow .md files explicitly
      return (!isAllowedType || !isAllowedSize);
    });

    if (invalidFiles.length > 0) {
      setUploadError(`Invalid files: ${invalidFiles.map(f => f.name).join(', ')}`);
      setUploading(false);
      return;
    }

    const user = auth.currentUser;
    if (!user) {
      setUploadError('User not authenticated');
      setUploading(false);
      return;
    }

    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });

    try {
      const response = await axios.post('/api/files/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'x-user-id': user.uid
        },
      });

      const uploadedFiles = response.data.uploadedFiles || [];
      setFiles(prev => [...prev, ...uploadedFiles]);

      // Track file upload usage
      if (uploadedFiles.length > 0) {
        try {
          await incrementUsage('fileUploads', uploadedFiles.length);
          await refreshUserData();
        } catch (usageError) {
          logger.error('Error tracking file upload usage:', usageError);
        }
      }

      // Automatically assign uploaded files to the chatbot if one is selected
      if (selectedChatbotId && uploadedFiles.length > 0) {
        const uploadedFileIds = uploadedFiles.map(file => file.id.toString());
        const currentAssignedIds = Array.from(assignedFileIds);
        const newAssignedIds = [...currentAssignedIds, ...uploadedFileIds];

        try {
          await axios.post(`/api/chatbot/${selectedChatbotId}/files`, {
            fileIds: newAssignedIds
          });

          setAssignedFileIds(new Set(newAssignedIds));
          logger.log('Uploaded files automatically assigned to chatbot');
        } catch (assignError) {
          logger.error('Error assigning files to chatbot:', assignError);
        }
      }
    } catch (error) {
      logger.error('Error uploading files:', error);
      setUploadError('File upload failed. Please check the file format and size.');
    } finally {
      setUploading(false);
    }
  };

  // Fetch chatbot files when selectedChatbotId changes
  useEffect(() => {
    if (selectedChatbotId) {
      fetchChatbotFiles();
    } else {
      setAssignedFileIds(new Set());
    }
  }, [selectedChatbotId, fetchChatbotFiles]);

  // Clear selection when files change
  useEffect(() => {
    setSelectedFileIds(new Set());
  }, [files]);

  const formatFileSize = (size) => {
    if (size <= 1024) return `${size} B`;
    if (size <= 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    if (size <= 1024 * 1024 * 1024) return `${(size / 1024 / 1024).toFixed(1)} MB`;
    return `${(size / 1024 / 1024 / 1024).toFixed(1)} GB`;
  };

  const getFileIcon = (mimeType) => {
    if (!mimeType) return faFileAlt;

    if (mimeType.includes('pdf')) return faFilePdf;
    if (mimeType.includes('word') || mimeType.includes('doc')) return faFileWord;
    if (mimeType.includes('excel') || mimeType.includes('sheet') || mimeType.includes('csv')) return faFileExcel;
    if (mimeType.includes('powerpoint') || mimeType.includes('ppt')) return faFilePowerpoint;
    if (mimeType.includes('image')) return faFileImage;
    if (mimeType.includes('zip') || mimeType.includes('archive')) return faFileArchive;
    if (mimeType.includes('json') || mimeType === 'application/json') return faFileAlt;
    if (mimeType === 'unknown') return faFileAlt;
    return faFileAlt;
  };

  // Toggle file selection
  const toggleFileSelection = (fileId) => {
    setSelectedFileIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fileId)) {
        newSet.delete(fileId);
      } else {
        newSet.add(fileId);
      }
      return newSet;
    });
  };

  // Select/Deselect all files
  const toggleSelectAll = () => {
    if (selectedFileIds.size === files.length) {
      setSelectedFileIds(new Set());
    } else {
      setSelectedFileIds(new Set(files.map(f => f.id)));
    }
  };

  // Open modal for single file delete
  const openDeleteModal = (file) => {
    setFilesToDelete([file]);
    setIsModalOpen(true);
  };

  // Open modal for bulk delete
  const openBulkDeleteModal = () => {
    const filesToDeleteList = files.filter(f => selectedFileIds.has(f.id));
    setFilesToDelete(filesToDeleteList);
    setIsModalOpen(true);
  };

  const closeConfirmationModal = () => {
    setFilesToDelete([]);
    setIsModalOpen(false);
  };

  const confirmDeletion = async () => {
    if (filesToDelete.length === 0) return;

    const user = auth.currentUser;
    if (!user) {
      setUploadError('User not authenticated');
      return;
    }

    setIsDeleting(true);
    setUploadError(null);

    try {
      // Delete files sequentially
      const deletePromises = filesToDelete.map(file =>
        axios.delete(`/api/files/${file.id}`, {
          headers: {
            'x-user-id': user.uid
          }
        })
      );

      const results = await Promise.allSettled(deletePromises);

      // Check for failures
      const failures = results.filter(r => r.status === 'rejected');
      const successes = results.filter(r => r.status === 'fulfilled');

      // Remove successfully deleted files from state
      const deletedIds = filesToDelete
        .filter((_, index) => results[index].status === 'fulfilled')
        .map(f => f.id);

      setFiles(prev => prev.filter(file => !deletedIds.includes(file.id)));
      setSelectedFileIds(prev => {
        const newSet = new Set(prev);
        deletedIds.forEach(id => newSet.delete(id));
        return newSet;
      });

      if (failures.length > 0) {
        setUploadError(`Failed to delete ${failures.length} file(s). ${successes.length} file(s) deleted successfully.`);
      }

      closeConfirmationModal();
    } catch (error) {
      logger.error('Error deleting files:', error);
      setUploadError('Failed to delete files. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRecrawl = async (file) => {
    if (!selectedChatbotId) return;
    setCrawlingSiteId(file.id);
    setUploadError(null);
    try {
      // Re-crawl the original URL that was saved
      // Determine if there are specific pages or just a main URL.
      // E.g., hitting scan then passing the URLs as train. We'll simply trigger a deep crawl:
      let targetUrl = file.original_name;
      if (!targetUrl.startsWith('http')) {
         targetUrl = `https://${targetUrl}`; 
      }
      
      const token = await auth.currentUser?.getIdToken();
      await axios.post('/api/files/crawl/start', {
        url: targetUrl,
        chatbotId: selectedChatbotId,
        maxDepth: 4,
        maxPages: 50
      }, {
        headers: {
          'x-user-id': auth.currentUser?.uid,
          'Authorization': `Bearer ${token}`,
        }
      });
      
      // Update UI momentarily
      logger.log('Crawl requested successfully');
      setUploadError(null);
      // Wait a moment then refresh state to show running
      setTimeout(fetchUploadedFiles, 2000);
    } catch (err) {
      logger.error('Error starting re-crawl:', err);
      // Backend sets specific HTTP errors (like 429 for quotas bounds)
      const errorMsg = err.response?.data?.error || 'Failed to start manual re-crawl.';
      setUploadError(errorMsg);
    } finally {
      setCrawlingSiteId(null);
    }
  };

  const renderFileItem = (file) => (
    <UniversalListItem
      key={file.id}
      secondaryAction={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {file.source === 'website' && (
            <Button
              variant="outlined"
              size="small"
              disabled={crawlingSiteId === file.id}
              onClick={() => handleRecrawl(file)}
              startIcon={crawlingSiteId === file.id ? <FontAwesomeIcon icon={faSpinner} spin /> : <RefreshCw size={14} />}
              sx={{ textTransform: 'none', borderRadius: '6px', fontSize: '13px', borderColor: '#e2e8f0', color: '#475569' }}
            >
              Manual Re-crawl
            </Button>
          )}
          <IconButton edge="end" size="small" onClick={() => openDeleteModal(file)} sx={{ color: '#ef4444' }}>
            <Trash2 size={16} />
          </IconButton>
        </Box>
      }
    >
      <Checkbox
        checked={selectedFileIds.has(file.id)}
        onChange={() => toggleFileSelection(file.id)}
        size="small"
        sx={{
          mr: 1,
          color: '#cbd5e1',
          '&.Mui-checked': {
            color: '#3b82f6',
          },
        }}
      />
      <ListItemIcon sx={{ minWidth: '40px' }}>
        {file.source === 'website' ? (
          <Globe size={20} color="#10b981" />
        ) : (
          <FontAwesomeIcon
            icon={getFileIcon(file.mimetype)}
            style={{ fontSize: '20px', color: '#3b82f6' }}
          />
        )}
      </ListItemIcon>
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <ItemTitle title={file.original_name || file.name}>
              {file.original_name || file.name}
            </ItemTitle>

            {/* Status Chips / Progress */}
            {file.processing_status === 'processing' ? (
              <ProgressContainer>
                <LinearProgress
                  variant="determinate"
                  value={Math.round(((file.processed_chunks || 0) / (file.total_chunks || 1)) * 100)}
                  sx={{
                    flex: 1,
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: '#e2e8f0',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: '#10b981',
                      borderRadius: 3
                    }
                  }}
                />
                <ProgressText>
                  {Math.round(((file.processed_chunks || 0) / (file.total_chunks || 1)) * 100)}%
                </ProgressText>
              </ProgressContainer>
            ) : file.processing_status === 'processed' ? (
              <StatusChip
                icon={<FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: '12px' }} />}
                label="Ready"
                size="small"
              />
            ) : file.processing_status === 'failed' ? (
              <StatusChip
                icon={<FontAwesomeIcon icon={faTimesCircle} style={{ fontSize: '12px' }} />}
                label="Failed"
                size="small"
                sx={{ bgcolor: '#fee2e2', color: '#dc2626', '& .MuiChip-icon': { color: '#dc2626' } }}
              />
            ) : (
              <StatusChip
                label="Uploaded"
                size="small"
                sx={{ bgcolor: '#e0f2fe', color: '#0284c7', '& .MuiChip-icon': { color: '#0284c7' } }}
              />
            )}
          </Box>
        }
        secondary={
          <Typography variant="caption" sx={{ color: '#64748b' }}>
            {formatFileSize(file.size)} • {new Date(file.created_at).toLocaleDateString()}
          </Typography>
        }
      />
    </UniversalListItem>
  );

  const rightPanelContent = (
    <KnowledgeBaseOverview selectedChatbotId={selectedChatbotId}>
      <input
        type="file"
        id="managed-file-upload"
        multiple
        style={{ display: 'none' }}
        onChange={handleUpload}
      />
      <label htmlFor="managed-file-upload" style={{ width: '100%' }}>
        <UploadButton component="span" disabled={uploading}>
          {uploading ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} />
              Uploading...
            </>
          ) : (
            <>
              <Upload size={18} style={{ marginRight: 8 }} />
              Upload Files
            </>
          )}
        </UploadButton>
      </label>
    </KnowledgeBaseOverview>
  );

  const emptyState = (
    <div className="files-empty-state" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: '40px' }}>
      <FontAwesomeIcon icon={faFileAlt} size="2x" style={{ color: '#cbd5e1', marginBottom: '12px' }} />
      <Typography variant="h6" sx={{ color: '#64748b', mb: 1, fontSize: '16px' }}>
        No files uploaded yet
      </Typography>
      <Typography sx={{ color: '#94a3b8', fontSize: '14px' }}>
        Upload files to get started with your managed storage
      </Typography>
    </div>
  );

  // Generate modal message
  const getModalMessage = () => {
    if (filesToDelete.length === 1) {
      return `Are you sure you want to delete "${filesToDelete[0]?.filename || filesToDelete[0]?.name}"? This action cannot be undone.`;
    }
    return `Are you sure you want to delete ${filesToDelete.length} files? This action cannot be undone.`;
  };

  const getModalTitle = () => {
    if (filesToDelete.length === 1) {
      return 'Delete File';
    }
    return `Delete ${filesToDelete.length} Files`;
  };

  return (
    <UniversalKnowledgeBasePanel
      title="Managed Files"
      subtitle="Manage files for your chatbots"
      rightPanelContent={rightPanelContent}
    >
      {uploadError && <Alert severity="error" sx={{ mb: 2, mx: 3, mt: 3 }}>{uploadError}</Alert>}

      <Box sx={{ p: 3 }}>
        {/* Selection Bar - Show when files exist */}
        {files.length > 0 && (
          <SelectionBar>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Checkbox
                checked={files.length > 0 && selectedFileIds.size === files.length}
                indeterminate={selectedFileIds.size > 0 && selectedFileIds.size < files.length}
                onChange={toggleSelectAll}
                size="small"
                sx={{
                  color: '#94a3b8',
                  '&.Mui-checked, &.MuiCheckbox-indeterminate': {
                    color: '#3b82f6',
                  },
                }}
              />
              <Typography sx={{ fontSize: '14px', color: '#475569' }}>
                {selectedFileIds.size > 0
                  ? `${selectedFileIds.size} of ${files.length} selected`
                  : `${files.length} file${files.length !== 1 ? 's' : ''}`
                }
              </Typography>
            </Box>
            {selectedFileIds.size > 0 && (
              <DeleteSelectedButton
                onClick={openBulkDeleteModal}
                disabled={isDeleting}
                startIcon={<Trash2 size={14} />}
              >
                {isDeleting ? 'Deleting...' : `Delete Selected (${selectedFileIds.size})`}
              </DeleteSelectedButton>
            )}
          </SelectionBar>
        )}

        <UniversalList
          items={files}
          renderItem={renderFileItem}
          emptyState={emptyState}
        />
      </Box>

      <ConfirmationModal
        isOpen={isModalOpen}
        onConfirm={confirmDeletion}
        onCancel={closeConfirmationModal}
        title={getModalTitle()}
        message={getModalMessage()}
      />
    </UniversalKnowledgeBasePanel>
  );
}

export default Files;
